import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const View = styled.div`
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 1em;

  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
  }

  select, input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  input {
    width: 200px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;

    select,
    input {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  ${adminTable};
  min-width: 640px;

  th,
  td {
    position: relative;
  }

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    border-bottom: 1px solid #ccc;
  }

  /* sort icons */
  th .sort-icons {
    display: inline-block;
    margin-left: 6px;
    vertical-align: middle;
    font-size: 10px;
    line-height: 1;
  }
  th .sort-icons span {
    cursor: pointer;
    display: block;
    line-height: 10px;
    color: #9CA3AF;
  }
  th .sort-icons .active {
    color: #A855F7;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px 10px;
  }

  a {
    color: ${({ colors }) => colors.buttonBackground};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;

  button {
    padding: 5px 10px;
    border: 1px solid ${({ colors }) => colors.border};
    background: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
      background: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const LinkButton = styled.span`
  font-size: 0.85em;
  margin-left: 0.5em;
  cursor: pointer;
  color: ${({ colors }) => colors.buttonBackground};
  border-bottom: 1px dotted ${({ colors }) => colors.buttonBackground};
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: ${LINK_HOVER_COLOR};
    border-bottom-color: ${LINK_HOVER_COLOR};
  }
`;

export default function RemoveTeacher({ colors, onRemoveClick: onExternalRemove }) {
  const [teachers, setTeachers]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [confirmOpen, setConfirm] = useState(false);
  const [resultOpen, setResult]   = useState(false);
  const [toRemove, setToRemove]   = useState(null);
  const [removalBusy, setRemovalBusy] = useState(false);
  const [resultTitle, setResultTitle] = useState("Removed");
  const [resultMessage, setResultMessage] = useState("Teacher removed successfully.");
  const [resultStatus, setResultStatus] = useState("Success");

  // filter/sort/pagination state
  const [searchTerm, setSearchTerm]     = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage]                 = useState(1);
  const [sortKey, setSortKey]           = useState("name");
  const [sortOrder, setSortOrder]       = useState("asc");
  const debouncedSearchTerm             = useDebouncedValue(searchTerm);

  const selectedSubscription =
    useSelector((s) => s.auth.selected_subscriptions) || {};
  const { invoice_id, customer_id, customer_code } = selectedSubscription;

  const fetchTeachers = useCallback(() => {
    if (!invoice_id) {
      setTeachers([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    API.getSubscriptionTeachers({
      invoice_id,
      customer_id,
      customer_code,
      page: itemsPerPage === -1 ? 0 : page - 1,
      items_per_page: itemsPerPage,
      order: (sortOrder || "asc").toUpperCase(),
      order_by: sortKey,
      search: debouncedSearchTerm,
    })
      .then((resp) => {
        const normalized = (resp.teachers || []).map((t) => {
          const gradeRecords = t.grade_codes || t.grade_records || [];
          const gradeList = Array.isArray(gradeRecords)
            ? gradeRecords
                .map((gr) =>
                  typeof gr === "string" ? gr : gr?.grade_code
                )
                .filter(Boolean)
                .join(", ")
            : "";

          const normalizedId =
            t.implementation_id ||
            t.id ||
            t.customer_user_id ||
            t.user_id;

          return {
            ...t,
            id:
              normalizedId,
            implementation_id:
              t.implementation_id ?? normalizedId,
            educator_name: t.name || t.educator_name || "",
            email: t.email || "",
            school_name: t.school_name || t.org_name || "",
            students_count: Number(
              t.num_of_students ?? t.students_count ?? 0
            ),
            grades_list: gradeList,
          };
        });

        setTeachers(normalized);
        setTotal(resp.total_teachers ?? resp.total ?? normalized.length);
      })
      .catch((err) => {
        console.error("Failed to load teachers for removal:", err);
        setTeachers([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [
    invoice_id,
    customer_id,
    customer_code,
    itemsPerPage,
    page,
    sortKey,
    sortOrder,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handlePageBoundary = useCallback(() => {
    if (itemsPerPage === -1) {
      if (page !== 1) {
        setPage(1);
      }
      return;
    }

    const newPageCount = Math.max(
      1,
      Math.ceil((total || 0) / (itemsPerPage || 1))
    );
    if (page > newPageCount) {
      setPage(newPageCount);
    }
  }, [itemsPerPage, page, total]);

  useEffect(() => {
    handlePageBoundary();
  }, [handlePageBoundary]);

  const handleRemoveClick = (teacher) => {
    if (loading || removalBusy) return;
    setToRemove(teacher);
    setConfirm(true);
  };

  const handleConfirm = () => {
    if (!toRemove || removalBusy) return;
    setConfirm(false);

    const payload = {
      customer_user_id: toRemove.customer_user_id,
      implementation_id: toRemove.implementation_id,
      user_id: toRemove.user_id,
    };

    if (
      payload.customer_user_id == null ||
      payload.implementation_id == null ||
      payload.user_id == null
    ) {
      console.error(
        "Missing identifiers for teacher removal:",
        toRemove
      );
      setResultTitle("Error");
      setResultMessage("Unable to remove teacher. Missing required identifiers.");
      setResultStatus("Failed");
      setResult(true);
      setToRemove(null);
      return;
    }

    setRemovalBusy(true);
    setResultTitle("Removed");
    setResultMessage("Teacher removed successfully.");
    setResultStatus("Success");

    API.removeSubscriptionTeacher(payload)
      .then((resp) => {
        const removeId = payload.implementation_id;
        const removeIdStr = String(removeId);
        setTeachers((ts) =>
          ts.filter(
            (t) =>
              String(t.implementation_id || t.id || t.user_id) !== removeIdStr
          )
        );
        setTotal((prev) => (prev > 0 ? prev - 1 : 0));
        onExternalRemove?.(removeId);
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setResultTitle(status === "Failed" ? "Error" : "Removed");
        setResultMessage(
          resp?.message ||
          (status === "Failed" ? "Failed to remove teacher." : "Teacher removed successfully.")
        );
        setResultStatus(status);
        setResult(true);
        fetchTeachers();
      })
      .catch((err) => {
        console.error("Failed to remove teacher:", err);
        setResultTitle("Error");
        setResultMessage(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to remove teacher."
        );
        setResultStatus("Failed");
        setResult(true);
      })
      .finally(() => {
        setRemovalBusy(false);
        setToRemove(null);
      });
  };

  const handleResultClose = () => {
    setResult(false);
    setResultStatus("Success");
  };

  const currentData = teachers;
  const pageCount =
    itemsPerPage === -1
      ? 1
      : Math.max(1, Math.ceil((total || 0) / (itemsPerPage || 1)));

  const start =
    total > 0
      ? itemsPerPage === -1
        ? 1
        : (page - 1) * itemsPerPage + 1
      : 0;
  const end =
    total > 0
      ? itemsPerPage === -1
        ? total
        : Math.min(start + currentData.length - 1, total)
      : 0;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const columns = [
    { key: "educator_name", label: "Educator", orderKey: "name" },
    { key: "school_name", label: "School Name", orderKey: "school_name" },
    {
      key: "students_count",
      label: "Students / Grades",
      orderKey: "students_count",
    },
  ];

  return (
    <View>
      {/* confirm removal */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Removal"
        message="Are you sure you want to delete educator? This user will no longer be displayed on the list of teachers within your implementation report."
        onCancel={() => {
          setConfirm(false);
          setToRemove(null);
        }}
        onConfirm={handleConfirm}
      />
      <ConfirmationModal
        show={resultOpen}
        title={resultTitle}
        message={resultMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />

      {/* filter controls */}
      <Controls colors={colors}>
        <div>
          <label style={{fontWeight: 'bold'}}>Show</label>
          <select
            value={itemsPerPage}
            onChange={e => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
            disabled={loading || removalBusy}
          >
            {[10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            <option value={-1}>All</option>
          </select>
          <label style={{marginLeft: '10px', fontWeight: 'bold'}}>entries</label>
        </div>
        <div>
          <label style={{fontWeight: 'bold'}}>Search:</label>
          <input
            type="text"
            placeholder="Type to filter..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            disabled={loading || removalBusy}
          />
        </div>
      </Controls>

      {/* table with sort icons */}
      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              {columns.map(col => {
                const isAscActive = sortKey===col.orderKey && sortOrder==="asc";
                const isDescActive = sortKey===col.orderKey && sortOrder==="desc";
                const defaultColor = "#9CA3AF";
                const activeColor = "#A855F7";
                return (
                  <th key={col.key}>
                    {col.label}
                    <span className="sort-icons">
                      <span
                        onClick={() => handleSort(col.orderKey)}
                        className={isAscActive ? "active":""}
                        style={{
                          color: isAscActive ? activeColor : defaultColor,
                          display: isDescActive ? "none" : "block"
                        }}
                      >
                        ▲
                      </span>
                      <span
                        onClick={() => handleSort(col.orderKey)}
                        className={isDescActive ? "active":""}
                        style={{
                          color: isDescActive ? activeColor : defaultColor,
                          display: isAscActive ? "none" : "block"
                        }}
                      >
                        ▼
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "1em" }}>
                  Loading teachers...
                </td>
              </tr>
            ) : removalBusy ? (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "1em" }}>
                  Removing teacher...
                </td>
              </tr>
            ) : currentData?.length > 0 ? (
              currentData.map(t => {
                const removeDisabled =
                  removalBusy ||
                  t.implementation_id == null ||
                  t.customer_user_id == null ||
                  t.user_id == null;

                return (
                  <tr key={t.implementation_id || t.id}>
                    <td>
                      <a href={`/admincentral/users/detail/${t.user_id}`}>
                        {t.educator_name}
                      </a>
                      <LinkButton
                        colors={colors}
                        style={{
                          color: "#E09723",
                          fontWeight: "bolder",
                          pointerEvents: removeDisabled ? "none" : "auto",
                          opacity: removeDisabled ? 0.5 : 1,
                          cursor: removeDisabled ? "default" : "pointer",
                        }}
                        onClick={() => !removeDisabled && handleRemoveClick(t)}
                      >
                        Remove
                      </LinkButton>
                      <br/>{t.email}
                    </td>
                    <td>{t.school_name}</td>
                    <td>
                      {t.students_count}<br/>
                      <i>{t.grades_list}</i>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "1em" }}>
                  No teachers to remove.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>

      {/* pagination info + controls */}
      <InfoText colors={colors}>
        Showing {start} to {end} of {total} entries
      </InfoText>
      <Pagination colors={colors}>
        <button
          onClick={() => setPage(p => Math.max(p-1,1))}
          disabled={page===1 || loading || removalBusy}
        >
          Previous
        </button>
        <button
          onClick={() => setPage(p => Math.min(p+1,pageCount))}
          disabled={page===pageCount || loading || removalBusy}
        >
          Next
        </button>
      </Pagination>
    </View>
  );
}
